import { Button, ButtonGroup, Checkbox, Intent } from "@blueprintjs/core";
import {
  DataTable,
  FieldSet,
  IDataTableColumn,
  InlineSearchCriteriaPanel,
  showError,
  useGridState,
  useSearchApi,
} from "nsitools-react";
import * as React from "react";

import {
  AffixExportGridDto,
  AffixRequestApi,
  AffixRequestExportApi,
  AffixRequestExportSearch,
  AffixRequestSearch,
  ERequestType,
  ErrorDetails,
  FileResponse,
} from "../../../../../api";
import { ExportButton } from "../../../../../components";
import { useApiService, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";
import { saveAs } from "file-saver";

export interface IAffixRequestExportListPageProps {
  requestType: ERequestType;
  exportFunc: (sObj: AffixRequestSearch) => Promise<FileResponse>;
}

export const AffixRequestExportListPage: React.FunctionComponent<IAffixRequestExportListPageProps> = ({
  requestType,
  exportFunc,
}) => {
  const api = useApiService(AffixRequestApi);
  const exportApi = useApiService(AffixRequestExportApi);
  const { t, tUnsafe } = useTl();
  const [sObjState, setsobjState] = React.useState<any>();
  const [isLoadingDoc, setisLoadingDoc] = React.useState(false);
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    availablePageSizes: [5, 15, 25],
    pageSize: 25,
    sortKeys: { requestNumber: "DESC" },
  });
  const searchFunction = React.useCallback(
    (sObj) => {
      setsobjState(sObj);
      return exportApi.searchAffixRequestExport(requestType, sObj);
    },
    [exportApi, requestType]
  );
  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
  });
  const { setData, data, totalCount } = tableState;

  const forceExport = React.useCallback(
    async (row: AffixExportGridDto, value: boolean) => {
      const dataToSet = [...data];
      const index = dataToSet.findIndex((d) => d.requestId === row.requestId);
      dataToSet[index]["toExport"] = !value;
      setData(dataToSet);
      return await api.forceExport(row.requestId, requestType, !value);
    },
    [api, data, requestType, setData]
  );

  const GenerateDocs = React.useCallback(async () => {
    setisLoadingDoc(true);
    try {
      const res = await api.getFciDocuments(requestType);
      saveAs(res.data, res.fileName);
      setisLoadingDoc(false);
      search();
    } catch (error) {
      setisLoadingDoc(false);
      const details: ErrorDetails = JSON.parse(error.response);
      if (details?.errorCode) {
        showError(tUnsafe(`BackendError_${details.errorCode}`));
      } else {
        showError(t(ETLCodes.Error));
      }
    }
  }, [api, requestType, search, t, tUnsafe]);

  const columns = React.useMemo<IDataTableColumn[]>(() => {
    switch (requestType) {
      case ERequestType.NewKennelRequest:
        return [
          {
            header: () => t(ETLCodes.Export),
            computed: true,
            disableSorting: true,
            fieldName: "toExport",
            autoFitContent: true,
            alignment: "center",
            render: (row: AffixExportGridDto) => (
              <Checkbox checked={row.toExport} onClick={() => forceExport(row, row.toExport)} />
            ),
          },
          {
            header: () => t(ETLCodes.RequestNumber),
            fieldName: "requestNumber",
          },
        ];
      case ERequestType.KennelImportRequest:
        return [
          {
            header: () => t(ETLCodes.Export),
            computed: true,
            disableSorting: true,
            fieldName: "toExport",
            autoFitContent: true,
            alignment: "center",
            render: (row: AffixExportGridDto) => (
              <Checkbox checked={row.toExport} onClick={() => forceExport(row, row.toExport)} />
            ),
          },
          {
            header: () => t(ETLCodes.RequestNumber),
            fieldName: "requestNumber",
          },
          {
            header: () => t(ETLCodes.Name),
            fieldName: "kennelName",
          },
          {
            header: () => t(ETLCodes.Affix),
            fieldName: "affix",
          },
          {
            header: () => t(ETLCodes.CountryOfOrigin),
            fieldName: "originCountry",
          },
        ];
      case ERequestType.KennelExportRequest:
        return [
          {
            header: () => t(ETLCodes.RequestNumber),
            fieldName: "requestNumber",
          },
          {
            header: () => t(ETLCodes.Name),
            fieldName: "kennelName",
          },
          {
            header: () => t(ETLCodes.Affix),
            fieldName: "affix",
          },
          {
            header: () => t(ETLCodes.Country),
            fieldName: "originCountry",
          },
        ];
      case ERequestType.KennelNameChangeRequest:
        return [
          {
            header: () => t(ETLCodes.Export),
            computed: true,
            disableSorting: true,
            fieldName: "toExport",
            autoFitContent: true,
            alignment: "center",
            render: (row: AffixExportGridDto) => (
              <Checkbox checked={row.toExport} onClick={() => forceExport(row, row.toExport)} />
            ),
          },
          {
            header: () => t(ETLCodes.RequestNumber),
            fieldName: "requestNumber",
          },
          {
            header: () => t(ETLCodes.Name),
            fieldName: "kennelName",
          },
          {
            header: () => t(ETLCodes.Affix),
            fieldName: "affix",
          },
          {
            header: () => t(ETLCodes.Name),
            fieldName: "oldKennelName",
          },
          {
            header: () => t(ETLCodes.Affix),
            fieldName: "oldAffix",
          },
        ];
      case ERequestType.KennelOwnershipChangeRequest:
        return [
          {
            header: () => t(ETLCodes.Export),
            computed: true,
            disableSorting: true,
            fieldName: "toExport",
            autoFitContent: true,
            alignment: "center",
            render: (row: AffixExportGridDto) => (
              <Checkbox checked={row.toExport} onClick={() => forceExport(row, row.toExport)} />
            ),
          },
          {
            header: () => t(ETLCodes.RequestNumber),
            fieldName: "requestNumber",
          },
          {
            header: () => t(ETLCodes.Name),
            fieldName: "kennelName",
          },
          {
            header: () => t(ETLCodes.Affix),
            fieldName: "affix",
          },
        ];
    }
  }, [forceExport, requestType, t]);

  const exportCallback = React.useCallback(
    async (sObj: any) => {
      var res = await exportFunc(sObj);
      search();
      return res;
    },
    [exportFunc, search]
  );

  const getCriteriasFunction = React.useCallback(() => exportApi.getSearchCriterias(true), [exportApi]);
  return (
    <FieldSet
      title={
        <div>
          {totalCount > 1
            ? t(ETLCodes.TableResults_Plural, { count: totalCount })
            : t(ETLCodes.TableResults, { count: totalCount })}
        </div>
      }
      rightElement={
        <ButtonGroup>
          <ExportButton exportFunction={exportCallback} sObj={sObjState} />
          <Button
            loading={isLoadingDoc}
            text={t(ETLCodes.FciDocument)}
            icon={<i className="far fa-file-word" />}
            onClick={GenerateDocs}
            minimal={true}
            intent={"primary"}
          />
        </ButtonGroup>
      }
    >
      {requestType === ERequestType.KennelExportRequest ? (
        <div style={{ marginBottom: "0.75rem" }}>
          <InlineSearchCriteriaPanel
            defaultCriterias={[
              {
                criteria: "Month",
                searchMode: "Equals",
                value: new Date().getMonth(),
              },
            ]}
            searchFunc={getCriteriasFunction}
            onSearch={search}
            triggerInitialSearch={true}
            removableCriteriaIntent={Intent.PRIMARY}
            showAllCriteriaRemovalButton={true}
            allCriteriaRemovalMode={"RemoveAll"}
            triggerSearchOnAllCriteriaRemoved={true}
            removeAllCriteriaButtonRGBColor="#a5c5d9"
          />
        </div>
      ) : null}
      <DataTable tableState={tableState} loading={loading} columns={columns} />
    </FieldSet>
  );
};
